import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// Lightweight JSON viewer without extra dependencies using native <details>/<summary>
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  Container,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Snackbar,
  Fab
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Settings as SettingsIcon, 
  Logout as LogoutIcon,
  Save as SaveIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  Home as HomeIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import StepSidebar, { SidebarHamburger } from '../components/StepSidebar';
import AIAssistantCreator from '../components/AIAssistantCreator';
import SystemArchitectModal from '../components/SystemArchitectModal';
import AssistantDefaultsModal from '../components/AssistantDefaultsModal';
import ZoomPanCanvas from '../components/ZoomPanCanvas';
import { Tabs, Tab } from '@mui/material';
import KnowledgeTasksView from '../components/KnowledgeTasksView';
import KnowledgeTasksSummary from '../components/KnowledgeTasksSummary';
import KnowledgeELOMapping from '../components/KnowledgeELOMapping';
import CurriculumNodeCanvas from '../components/CurriculumNodeCanvas';
import StoryBoardFlow from '../components/StoryBoardFlow';
import CurriculumTreeView from '../components/CurriculumTreeView';
import CurrentAssistantInfo from '../components/CurrentAssistantInfo';
import ErrorBoundary from '../components/ErrorBoundary';
import ContentCreationStep from '../components/ContentCreationStep';
import CurriculumSummaryModal from '../components/CurriculumSummaryModal';
import Step1Interface from '../components/Step1Interface';
import CurriculumAnalysisStep from '../components/CurriculumAnalysisStep';
import QualificationDetails from '../components/QualificationDetails';
import JsonTreeModal from '../components/JsonTreeModal';
// import { saveAs } from 'file-saver'; // duplicated import removed below
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

/**
 * Step2 is a pattern for step-based workflow. To extend for other steps, create similar components (Step3, Step4, etc.)
 * Each should check for an assistant, extract step-specific data, display/edit/save as needed.
 */
// Step2 is now the ELO Mapping Canvas (Step 3 in new flow) - Canvas only, no tabs
function Step2({ qualificationId, uploadedFileInfo, eloFileInfo, hasExitLevelOutcomes, curriculumHasExitLevelOutcomes, onAdvanceStep, onRefreshSteps, setPreviewData, setPreviewOpen, setJsonTreeModalOpen, shouldStartStreaming, setShouldStartStreaming }) {
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [loadingBaseMessage, setLoadingBaseMessage] = useState('');
  const [loaderTick, setLoaderTick] = useState(0);
  const [pendingCanvasNav, setPendingCanvasNav] = useState(false);
  const [error, setError] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [viewMode, setViewMode] = useState('canvas'); // 'canvas' or 'tasks'
  const [showTasksOnCanvas, setShowTasksOnCanvas] = useState(false); // New state for showing tasks on canvas
  const [showSummaryView, setShowSummaryView] = useState(true); // State for showing summary vs detailed view
  const [showMappingView, setShowMappingView] = useState(false); // State for showing knowledge-ELO mapping view
  const [showCurriculumTreeView, setShowCurriculumTreeView] = useState(false); // State for showing curriculum tree view
  // Removed step2Tab state - this component now only shows canvas
  const [codeExamples, setCodeExamples] = useState({});
  const [curriculumSummary, setCurriculumSummary] = useState(null); // State for storing curriculum analysis results
  const [showNodeCanvas, setShowNodeCanvas] = useState(false); // Node-based design layout
  const [reqNodes, setReqNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [allApproved, setAllApproved] = useState(false);
  const [approvedCount, setApprovedCount] = useState(0);
  const [approvableCount, setApprovableCount] = useState(0);
  const [newCurriculumData, setNewCurriculumData] = useState(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [showAssignDebug, setShowAssignDebug] = useState(false);
  const [assignLog, setAssignLog] = useState([]);
  const [assignWaiting, setAssignWaiting] = useState(false);
  const [showAssignIndicator, setShowAssignIndicator] = useState(false);
  const [assignIndicatorText, setAssignIndicatorText] = useState('');
  // Table extraction config (moved from Step 1 to Step 2 setup)
  const [tabledDoc, setTabledDoc] = useState(false);
  const [tableHeaders, setTableHeaders] = useState({ code: '', title: '', credits: '', weight: '', nqf: '' });
  const [fixedColumns, setFixedColumns] = useState('');
  const canvasRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [assignReasons, setAssignReasons] = useState({}); // key: `${elo}|${type}|${code}` -> { reason, confidence, source }
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [qualificationTitle, setQualificationTitle] = useState('');
  const [tempColor, setTempColor] = useState(null); // Temporary color for preview in color picker
  // Load existing table extraction config for this qualification
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}/table-config`);
        const cfg = res.data && res.data.table_extraction_config;
        if (cfg && typeof cfg === 'object') {
          setTabledDoc(!!cfg.enabled);
          const h = cfg.headers || {};
          setTableHeaders({
            code: h.code || '',
            title: h.title || '',
            credits: h.credits || '',
            weight: h.weight || '',
            nqf: h.nqf || ''
          });
          const fx = Array.isArray(cfg.fixed_columns) ? cfg.fixed_columns.join(', ') : '';
          setFixedColumns(fx);
        }
      } catch {}
    })();
  }, [qualificationId]);
  const [curriculumModalOpen, setCurriculumModalOpen] = useState(false);
  const [qualificationLastEdit, setQualificationLastEdit] = useState('');
  // Read-only shared canvas view detection (local to Step2)
  const isReadOnlyView = (() => { try { const url = new URL(window.location.href); return url.searchParams.get('readonly') === '1'; } catch { return false; } })();

  // Load qualification title for share label
  useEffect(() => {
    if (!qualificationId) return;
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}`);
        setQualificationTitle(res.data?.title || '');
        const last = res.data?.updated_at || res.data?.updatedAt || res.data?.created_at || res.data?.createdAt;
        if (last) {
          try { setQualificationLastEdit(new Date(last).toLocaleString()); } catch { setQualificationLastEdit(String(last)); }
        } else {
          setQualificationLastEdit('');
        }
      } catch {}
    })();
  }, [qualificationId]);
  
  // Assignment streaming - only triggered by button click, not on mount/refresh
  const streamingRef = useRef(false); // Guard against duplicate connections
  const eventSourceRef = useRef(null); // Store EventSource for cleanup
  // shouldStartStreaming is now passed as a prop from QualificationPage
  
  useEffect(() => {
    // Only run if explicitly triggered by button click
    if (!qualificationId || !shouldStartStreaming || streamingRef.current) return;
    
    streamingRef.current = true;
    
    // Start AI assignment streaming
    const startStreaming = async () => {
      try {
        setAssignWaiting(true);
        setShowAssignIndicator(true);
        setAssignIndicatorText('AI is mapping curriculum to ELOs...');
        
        // Get JWT token for authenticated SSE
        const token = localStorage.getItem('token');
        
        // EventSource for SSE (baseURL already includes /api from axios config)
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const esUrl = token
          ? `${baseUrl}/qualifications/${qualificationId}/assignments/stream?token=${encodeURIComponent(token)}`
          : `${baseUrl}/qualifications/${qualificationId}/assignments/stream`;
        

        const es = new EventSource(esUrl);
        eventSourceRef.current = es; // Store for cleanup
        
        es.addEventListener('start', (e) => {

          setAssignIndicatorText('Assignment streaming started...');
        });
        
        es.addEventListener('debug', (e) => {
          try {
            const data = JSON.parse(e.data);

            if (data.stage) {
              setAssignIndicatorText(`Processing: ${data.stage}...`);
            }
          } catch {}
        });
        
        es.addEventListener('assigned', (e) => {
          try {
            const data = JSON.parse(e.data);

            
            // Update UI immediately with new assignment
            const { elo_index, type, code, reason, confidence } = data;
            
            // Update outcomes state
            setOutcomes(prev => {
              const updated = [...prev];
              if (updated[elo_index]) {
                const outcome = updated[elo_index];
                
                // Add code to appropriate array
                if (type === 'KT' && !outcome.knowledge_tasks.includes(code)) {
                  outcome.knowledge_tasks = [...outcome.knowledge_tasks, code];
                } else if (type === 'PS' && !outcome.practical_work.includes(code)) {
                  outcome.practical_work = [...outcome.practical_work, code];
                } else if (type === 'WE' && !outcome.workplace_experience.includes(code)) {
                  outcome.workplace_experience = [...outcome.workplace_experience, code];
                }
                
                // Update assignment_meta
                outcome.assignment_meta = outcome.assignment_meta || {};
                outcome.assignment_meta[code] = { type, source: 'ai', reason, confidence };
              }
              return updated;
            });
            
            // Update assignReasons for the reason/confidence display
            setAssignReasons(prev => {
              const key = `${elo_index}|${type}|${code}`;
              return {
                ...prev,
                [key]: { reason, confidence, source: 'ai' }
              };
            });
            
            setAssignIndicatorText(`Assigned ${code} to ELO ${elo_index + 1}`);
          } catch (err) {

          }
        });
        
        es.addEventListener('done', async (e) => {

          es.close();
          eventSourceRef.current = null;
          setAssignWaiting(false);
          setAssignIndicatorText('Assignment complete!');
          streamingRef.current = false; // Reset guard
          
          // Hide indicator after 2 seconds
          setTimeout(() => setShowAssignIndicator(false), 2000);
          
          // Don't reload outcomes - we've been updating them in real-time via 'assigned' events
          // This prevents overwriting the live updates

        });
        
        es.addEventListener('error', (e) => {
          console.error('[Step2] EventSource error event:', e);
          console.error('[Step2] EventSource readyState:', es.readyState);
          console.error('[Step2] EventSource URL:', esUrl);
          if (e.data) {
            try {
              const errorData = JSON.parse(e.data);
              console.error('[Step2] Error data:', errorData);
              setError(`Assignment failed: ${errorData.message || errorData.stage || 'Unknown error'}`);
            } catch (parseErr) {
              console.error('[Step2] Error data (raw):', e.data);
            }
          }
          es.close();
          eventSourceRef.current = null;
          setAssignWaiting(false);
          setAssignIndicatorText('Assignment failed');
          streamingRef.current = false; // Reset guard
          setTimeout(() => setShowAssignIndicator(false), 3000);
        });
        
        es.onerror = (error) => {
          console.error('[Step2] EventSource onerror:', error);
          console.error('[Step2] EventSource readyState:', es.readyState);
          console.error('[Step2] EventSource URL:', esUrl);
          console.error('[Step2] EventSource status:', es.readyState === EventSource.CONNECTING ? 'CONNECTING' : es.readyState === EventSource.OPEN ? 'OPEN' : 'CLOSED');
          es.close();
          eventSourceRef.current = null;
          setAssignWaiting(false);
          setAssignIndicatorText('Connection error');
          setError('Failed to connect to assignment service. Check console for details.');
          streamingRef.current = false; // Reset guard
          setTimeout(() => setShowAssignIndicator(false), 3000);
        };
        
      } catch (err) {
        console.error('[Step2] Failed to start EventSource:', err);
        console.error('[Step2] Error stack:', err.stack);
        setAssignWaiting(false);
        setAssignIndicatorText('Failed to start');
        setError(`Failed to start assignment: ${err.message || 'Unknown error'}`);
        streamingRef.current = false; // Reset guard
        setTimeout(() => setShowAssignIndicator(false), 3000);
      }
    };
    
    startStreaming();
    
    // Cleanup on unmount
    return () => {

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      streamingRef.current = false;
      setShouldStartStreaming(false); // Reset trigger
    };
  }, [qualificationId, shouldStartStreaming]); // Only run when explicitly triggered
  
  const markUserAssign = (eloIdx, type, code, reason, confidence) => {
    const key = `${eloIdx}|${type}|${code}`;
    setAssignReasons(prev => ({ 
      ...prev, 
      [key]: { 
        reason: reason ?? (prev[key]?.reason || ''), 
        confidence: (typeof confidence==='number'?confidence:(prev[key]?.confidence||null)), 
        source: 'user',
        credits: prev[key]?.credits || 0,
        notional_hours: prev[key]?.notional_hours || 0,
        active_hours: prev[key]?.active_hours || 0,
        weight_pct: prev[key]?.weight_pct || 0
      } 
    }));
  };

  // Export current mapped structure to CSV (scoped to Step2)
  const handleExportCSV = () => {
    try {
      const mapped = canvasRef.current?.getMappedStructure?.();
      if (!mapped || !Array.isArray(mapped.columns)) return;
      const rows = [];
      mapped.columns.forEach(col => {
        const pushRow = (type, it) => rows.push({ elo_number: col.elo_number, module_title: col.module_title, type, code: it.code, title: it.title, credits: it.credits || 0, weight_pct: it.weight_pct || 0 });
        (col.knowledge_tasks || []).forEach(it => pushRow('KT', it));
        (col.practical_work || []).forEach(it => pushRow('PS', it));
        (col.workplace_experience || []).forEach(it => pushRow('WA', it));
      });
      const headers = ['elo_number','module_title','type','code','title','credits','weight_pct'];
      const csv = [headers.join(',')].concat(rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g,'""')}"`).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'elo-mapping.csv');
    } catch {}
  };

  // Handler to regenerate assignments only (clear and re-stream)
  const handleRegenerateAssignmentsOnly = async () => {
    if (!window.confirm('This will clear all existing assignments and regenerate them. Do you want to continue?')) {
      return;
    }
    
    try {
      // Clear all assignments from outcomes
      const clearedOutcomes = outcomes.map(o => ({
        ...o,
        knowledge_tasks: [],
        practical_work: [],
        workplace_experience: [],
        assignment_meta: {}
      }));
      
      // Save cleared outcomes
      await api.put(`/qualifications/${qualificationId}/outcomes`, { outcomes: clearedOutcomes });
      setOutcomes(clearedOutcomes);
      
      // Clear assignment reasons
      setAssignReasons({});
      
      // Trigger streaming to regenerate assignments
      setShouldStartStreaming(true);
    } catch (error) {

      setError('Failed to regenerate assignments');
    }
  };

  // Handler to regenerate both ELOs and assignments
  const handleRegenerateAll = async () => {
    if (!window.confirm('This will regenerate ELOs and clear all assignments. Do you want to continue?')) {
      return;
    }
    
    try {
      setLoading(true);
      // Clear outcomes first
      setOutcomes([]);
      // Regenerate ELOs
      await handleRegenerateOutcomes();
      // Clear assignment reasons
      setAssignReasons({});
      // After ELOs are regenerated, trigger assignment streaming
      setTimeout(() => {
        setShouldStartStreaming(true);
      }, 500);
    } catch (error) {

      setError('Failed to regenerate');
      setLoading(false);
    }
  };

  // Share read-only canvas link
  const handleShareReadOnly = async () => {
    try {
      // Ask backend for a share token
      const res = await api.post(`/qualifications/${qualificationId}/share-token`);
      const token = res.data?.token;
      const url = new URL(window.location.href);
      url.searchParams.set('readonly', '1');
      url.searchParams.set('view', 'canvas');
      if (token) url.searchParams.set('token', token);
      setShareLink(url.toString());
      setShareDialogOpen(true);
    } catch {}
  };

  const computeApprovalStats = (nodes) => {
    const relevant = (nodes || []).filter(n => n && (n.node_type === 'topic_kt' || n.node_type === 'practical_skill' || n.node_type === 'work_experience_item'));
    const total = relevant.length;
    const approved = relevant.filter(n => !!n.approved).length;
    return { total, approved, all: total > 0 && approved === total };
  };

  const refreshRequirementNodes = async () => {
    try {
      setNodesLoading(true);
      const res = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
      const nodes = Array.isArray(res.data?.nodes) ? res.data.nodes : [];
      setReqNodes(nodes);
      const stats = computeApprovalStats(nodes);
      setAllApproved(stats.all);
      setApprovedCount(stats.approved);
      setApprovableCount(stats.total);
    } catch (e) {
      // noop
    } finally {
      setNodesLoading(false);
    }
  };

  const fetchNewCurriculumData = async () => {
    if (!qualificationId) return;
    try {
      const response = await api.get(`/qualifications/${qualificationId}/curriculum-tree/local?t=${Date.now()}`);
      if (response.data && response.data.tree) {
        setNewCurriculumData(response.data.tree);
      }
    } catch (error) {

      setNewCurriculumData(null);
    }
  };

  // Fetch new curriculum data and load nodes when component mounts
  useEffect(() => {
    if (qualificationId) {
      fetchNewCurriculumData();
        refreshRequirementNodes();
    }
  }, [qualificationId]);

  // Load saved code examples for this qualification
  useEffect(() => {
    if (!qualificationId) return;
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}/code-examples`);
        if (res.data && res.data.code_examples) setCodeExamples(res.data.code_examples);
      } catch {}
    })();
  }, [qualificationId]);

  // Rotate humorous loader suffix every ~1.2s when overlay is active
  useEffect(() => {
    if (!loadingOverlay) return;
    const t = setInterval(() => setLoaderTick(tk => tk + 1), 1200);
    return () => clearInterval(t);
  }, [loadingOverlay]);

  // Determine which document to use for ELO extraction
  const getEloDocumentInfo = () => {
    
    // Priority 1: ELO document exists and has exit level outcomes
    if (eloFileInfo && hasExitLevelOutcomes) {
      return { type: 'elo', info: eloFileInfo };
    }
    
    // Priority 2: Curriculum document has exit level outcomes
    if (uploadedFileInfo && curriculumHasExitLevelOutcomes) {
      return { type: 'curriculum', info: uploadedFileInfo };
    }
    
    // Priority 3: ELO document exists (regardless of flag)
    if (eloFileInfo) {
      return { type: 'elo', info: eloFileInfo };
    }
    
    // Priority 4: Curriculum document exists (fallback)
    if (uploadedFileInfo) {
      return { type: 'curriculum', info: uploadedFileInfo };
    }
    
    // No document available
    return { type: null, info: null };
  };

  // Fetch outcomes on mount
  useEffect(() => {
    const fetchOutcomes = async () => {
      try {
        const response = await api.get(`/qualifications/${qualificationId}/outcomes`);
        const outs = Array.isArray(response.data?.outcomes) ? response.data.outcomes : [];
        setOutcomes(outs);
        // hydrate assignReasons from _assignment_meta for tooltips/pills
        const meta = {};
        outs.forEach((o, i) => {
          const m = o?._assignment_meta || {};
          Object.keys(m).forEach(code => {
            const ent = m[code] || {};
            const key = `${i}|${ent.type||'KT'}|${code}`;
            meta[key] = { 
              reason: ent.reason || '', 
              confidence: (typeof ent.confidence === 'number' ? ent.confidence : null), 
              source: ent.source || 'ai',
              credits: ent.credits || 0,
              notional_hours: ent.notional_hours || 0,
              active_hours: ent.active_hours || 0,
              weight_pct: ent.weight_pct || 0
            };
          });
        });
        setAssignReasons(meta);
      } catch (error) {

      }
    };
    fetchOutcomes();
  }, [qualificationId]);

  // Function to trigger outcome extraction
  const handleExtractOutcomes = async () => {
    setLoading(true);
    setLoadingOverlay(true);
    setLoadingBaseMessage('Preparing assistant…');
    setError('');
    
    const documentInfo = getEloDocumentInfo();
    
    if (!documentInfo.info) {
      setError('No document available for ELO extraction. Please upload a document or flag an existing document as containing ELOs.');
      setLoading(false);
      setLoadingOverlay(false);
      return false;
    }
    
    
    try {
      // First, ensure the step 2 assistant exists
      setLoadingBaseMessage('Ensuring assistant exists…');
      await api.get(`/qualifications/${qualificationId}/assistant/step_2`);
      
      // Use the dedicated Step 2 EOL extraction endpoint
      setLoadingBaseMessage('Starting ELO extraction…');
      const response = await api.post(`/qualifications/${qualificationId}/extract-eols`, {
        prompt: `Extract Exit Level Outcomes (ELOs) from the attached ${documentInfo.type} document. Look for ELOs labeled as EOL1, EOL2, EOL3, etc. Extract each ELO with its complete information including the ELO number, title, and description. For each ELO, provide a comprehensive and detailed description that explains what the learner will achieve upon completion of this outcome. Also suggest an appropriate module title that reflects the content and purpose of that outcome. Do not extract Knowledge Tasks (KTs), subtasks, or any other curriculum elements. Focus only on ELOs and their suggested module names.`
      });
      
      if (response.data.success) {
        const extractedData = response.data.outcomes;
        
        // Handle the response data - it's already parsed by the backend
        let parsedOutcomes = [];
        try {
          // The backend already parses the JSON and returns the outcomes directly
          if (Array.isArray(extractedData)) {
            // If it's already an array, use it directly
            parsedOutcomes = extractedData;
          } else if (typeof extractedData === 'object' && extractedData !== null) {
            // If it's an object, extract the outcomes array
            parsedOutcomes = extractedData.ELOs || extractedData.outcomes || extractedData.eols || [];
          } else if (typeof extractedData === 'string') {
            // If it's still a string (fallback), try to parse it
            const match = extractedData.match(/```json\s*([\s\S]*?)\s*```/);
            if (match) {
              const jsonString = match[1];
              const parsed = JSON.parse(jsonString);
              parsedOutcomes = parsed.ELOs || parsed.outcomes || parsed.eols || [];
            } else {
              // Try to parse directly if it's already JSON
              const parsed = JSON.parse(extractedData);
              parsedOutcomes = parsed.ELOs || parsed.outcomes || parsed.eols || [];
            }
          }
        } catch (e) {


          setError('Failed to parse EOLs data');
          return;
        }
        
        
        // Map the extracted data to our outcomes format
        const mappedOutcomes = parsedOutcomes.map((outcome, index) => {
          return {
            id: index + 1,
            outcome: outcome.title || outcome.ELO_number || `EOL${index + 1}`,
            // Use derived module name as the title if available
            title: outcome.suggested_module_title || outcome.title || outcome.ELO_number || `EOL${index + 1}`,
            subtitle: outcome.subtitle || '',
            description: outcome.description || '',
            suggested_module_title: outcome.suggested_module_title || '',
            elo_number: outcome.ELO_number || outcome.number || `EOL${index + 1}`,
            knowledge_tasks: Array.isArray(outcome.knowledge_tasks) ? outcome.knowledge_tasks : [],
            assessments: Array.isArray(outcome.assessments) ? outcome.assessments : [],
            practical_work: Array.isArray(outcome.practical_work) ? outcome.practical_work : [],
            workplace_experience: Array.isArray(outcome.workplace_experience) ? outcome.workplace_experience : [],
            color: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        });
        
        
        // Fetch from backend to include server-enriched fields and mappings
        try {
          setLoadingBaseMessage('Finalising assignments…');
          const fresh = await api.get(`/qualifications/${qualificationId}/outcomes`);
          setOutcomes(Array.isArray(fresh.data?.outcomes) ? fresh.data.outcomes : mappedOutcomes);
        } catch {
          setOutcomes(mappedOutcomes);
        }
        return true;
      }
    } catch (error) {

      setError(error.response?.data?.message || 'Failed to extract EOLs');
      return false;
    } finally {
      setLoading(false);
      setLoadingBaseMessage('');
      setLoadingOverlay(false);
    }
  };

  // Function to regenerate outcomes (clear existing and re-extract)
  const handleRegenerateOutcomes = async () => {
    setLoading(true);
    setError('');
    
    const documentInfo = getEloDocumentInfo();
    
    if (!documentInfo.info) {
      setError('No document available for ELO extraction. Please upload a document or flag an existing document as containing ELOs.');
      setLoading(false);
      return;
    }
    
    
    try {
      // Clear existing outcomes first
      setOutcomes([]);
      
      // Use the dedicated Step 2 EOL extraction endpoint
      const response = await api.post(`/qualifications/${qualificationId}/extract-eols`, {
        prompt: `Extract Exit Level Outcomes (ELOs) from the attached ${documentInfo.type} document. Look for ELOs labeled as EOL1, EOL2, EOL3, etc. Extract each ELO with its complete information including the ELO number, title, and description. For each ELO, provide a comprehensive and detailed description that explains what the learner will achieve upon completion of this outcome. Also suggest an appropriate module title that reflects the content and purpose of that outcome. Do not extract Knowledge Tasks (KTs), subtasks, or any other curriculum elements. Focus only on ELOs and their suggested module names.`
      });
      
      if (response.data.success) {
        const extractedData = response.data.outcomes;
        
        // Handle the response data - it's already parsed by the backend
        let parsedOutcomes = [];
        try {
          // The backend already parses the JSON and returns the outcomes directly
          if (Array.isArray(extractedData)) {
            // If it's already an array, use it directly
            parsedOutcomes = extractedData;
          } else if (typeof extractedData === 'object' && extractedData !== null) {
            // If it's an object, extract the outcomes array
            parsedOutcomes = extractedData.ELOs || extractedData.outcomes || extractedData.eols || [];
          } else if (typeof extractedData === 'string') {
            // If it's still a string (fallback), try to parse it
            const match = extractedData.match(/```json\s*([\s\S]*?)\s*```/);
            if (match) {
              const jsonString = match[1];
              const parsed = JSON.parse(jsonString);
              parsedOutcomes = parsed.ELOs || parsed.outcomes || parsed.eols || [];
            } else {
              // Try to parse directly if it's already JSON
              const parsed = JSON.parse(extractedData);
              parsedOutcomes = parsed.ELOs || parsed.outcomes || parsed.eols || [];
            }
          }
        } catch (e) {


          setError('Failed to parse regenerated EOLs data');
          return;
        }
        
        
        // Map the extracted data to our outcomes format
        const mappedOutcomes = parsedOutcomes.map((outcome, index) => {
          return {
            id: index + 1,
            outcome: outcome.title || outcome.ELO_number || `EOL${index + 1}`,
            // Use derived module name as the title if available
            title: outcome.suggested_module_title || outcome.title || outcome.ELO_number || `EOL${index + 1}`,
            subtitle: outcome.subtitle || '',
            description: outcome.description || '',
            suggested_module_title: outcome.suggested_module_title || '',
            elo_number: outcome.ELO_number || outcome.number || `EOL${index + 1}`,
            knowledge_tasks: Array.isArray(outcome.knowledge_tasks) ? outcome.knowledge_tasks : [],
            assessments: Array.isArray(outcome.assessments) ? outcome.assessments : [],
            practical_work: Array.isArray(outcome.practical_work) ? outcome.practical_work : [],
            workplace_experience: Array.isArray(outcome.workplace_experience) ? outcome.workplace_experience : [],
            color: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        });
        
        // Fetch persisted outcomes (backend enriches with mappings)
        try {
          const fresh = await api.get(`/qualifications/${qualificationId}/outcomes`);
          setOutcomes(Array.isArray(fresh.data?.outcomes) ? fresh.data.outcomes : mappedOutcomes);
        } catch {
          setOutcomes(mappedOutcomes);
        }
      }
    } catch (error) {

      setError(error.response?.data?.message || 'Failed to regenerate outcomes');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle "Show Tasks" button click - extract and display tasks on canvas
  const handleShowTasksOnCanvas = async () => {
    if (outcomes.length === 0) {
      setError('No outcomes available. Please extract outcomes first.');
      return;
    }

    // Check if outcomes already have knowledge_tasks
    const hasTasks = outcomes.some(outcome => outcome.knowledge_tasks && outcome.knowledge_tasks.length > 0);
    
    if (!hasTasks) {
      setLoading(true);
      setError('');
      
      try {
        // Extract Knowledge Tasks from the document
        const response = await api.post(`/qualifications/${qualificationId}/analyze`, {
          prompt: `Extract ONLY the Knowledge Tasks (KT) and their subtasks from the attached qualification document. Look for patterns like "KM-01-KT01", "KM-01-KT02", etc., and their corresponding subtasks like "KT0101", "KT0102", etc. For each KM module, extract all its Knowledge Tasks with their titles, descriptions, and all associated subtasks with their descriptions and weights. Respond in JSON format: { "outcomes": [ { "title": "252901001-KM-01: [Module Title]", "knowledge_tasks": [ { "code": "KM-01-KT01", "title": "[Task Title]", "description": "[Task Description]", "subtasks": [ { "code": "KT0101", "title": "[Subtask Title]", "description": "[Subtask Description]", "weight": "[Weight if specified]" }, ... ] }, ... ] }, ... ] }`
        });
        
        if (response.data.success) {
          const extractedData = response.data.data;
          
          // Update existing outcomes with the extracted knowledge tasks
          const updatedOutcomes = outcomes.map(outcome => {
            const matchingOutcome = extractedData.outcomes.find(extracted => 
              extracted.title === outcome.outcome || extracted.title.includes(outcome.outcome.split(':')[0])
            );
            return {
              ...outcome,
              knowledge_tasks: matchingOutcome ? matchingOutcome.knowledge_tasks || [] : outcome.knowledge_tasks || []
            };
          });
          
                  setOutcomes(updatedOutcomes);
        
        // Save to database using PUT endpoint
        await api.put(`/qualifications/${qualificationId}/outcomes`, { outcomes: updatedOutcomes });
          
        }
      } catch (error) {

        setError(error.response?.data?.message || 'Failed to extract Knowledge Tasks');
      } finally {
        setLoading(false);
      }
    }
    
    // Toggle the display of tasks on canvas
    setShowTasksOnCanvas(!showTasksOnCanvas);
  };

  // Fetch persisted curriculum summary so user doesn't have to regenerate every time
  useEffect(() => {
    if (!qualificationId) return;
    // Only pull if not already set
    if (curriculumSummary) return;
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}/curriculum-summary`);
        if (res.data && res.data.curriculum_summary) {
          setCurriculumSummary(res.data.curriculum_summary);
        }
      } catch (e) {
        // silent fallback; user can still run analysis
      }
    })();
  }, [qualificationId, curriculumSummary]);

  // Handler for selecting a card
  const handleSelectCard = (idx) => {
    setSelectedIdx(idx);
  };

  // Handler for opening color picker
  const handleOpenColorPicker = () => {
    if (selectedIdx !== null) {
      setTempColor(outcomes[selectedIdx]?.color || '#e3f2fd');
      setColorPickerOpen(true);
    }
  };

  // Handler for changing color (temporary, for preview)
  const handleChangeColor = (color) => {
    setTempColor(color);
  };

  // Handler for confirming color change
  const handleConfirmColor = () => {
    if (selectedIdx !== null && tempColor !== null) {
      const updated = [...outcomes];
      updated[selectedIdx] = {
        ...updated[selectedIdx],
        color: tempColor
      };
      setOutcomes(updated);
      setColorPickerOpen(false);
      setTempColor(null);
      // Save to DB
      api.put(`/qualifications/${qualificationId}/outcomes`, { outcomes: updated });
    }
  };

  // Handler for opening edit modal - accepts optional index to ensure correct data is loaded
  const handleOpenEditModal = (idx = null) => {
    const targetIdx = idx !== null ? idx : selectedIdx;
    if (targetIdx !== null && outcomes[targetIdx]) {
      setEditTitle(outcomes[targetIdx].suggested_module_title || '');
      setEditDescription(outcomes[targetIdx].description || '');
      setSelectedIdx(targetIdx); // Ensure selectedIdx is set
      setEditModalOpen(true);
    }
  };

  // Handler for saving edit
  const handleSaveEdit = () => {
    if (selectedIdx !== null) {
      const updated = [...outcomes];
      // Create a new object for the updated outcome to ensure React detects the change
      updated[selectedIdx] = {
        ...updated[selectedIdx],
        suggested_module_title: editTitle,
        description: editDescription
      };
      setOutcomes(updated);
      setEditModalOpen(false);
      // Save to DB
      api.put(`/qualifications/${qualificationId}/outcomes`, { outcomes: updated });
    }
  };

  // Handler for updating module title
  const handleUpdateModuleTitle = (moduleTitle) => {
    if (selectedIdx !== null) {
      const updated = [...outcomes];
      // Create a new object for the updated outcome to ensure React detects the change
      updated[selectedIdx] = {
        ...updated[selectedIdx],
        suggested_module_title: moduleTitle
      };
      setOutcomes(updated);
      // Save to DB
      api.put(`/qualifications/${qualificationId}/outcomes`, { outcomes: updated });
    }
  };

  // Handler for curriculum analysis
  const handleCurriculumAnalysis = async (qualificationId) => {
    try {
      // Reset previous analysis before running new one
      try { await api.post(`/qualifications/${qualificationId}/analyze-curriculum/reset`); } catch {}
      const response = await api.post(`/qualifications/${qualificationId}/analyze-curriculum`);
      if (response.data.success) {
        setCurriculumSummary(response.data.curriculum_summary);
      }
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.message || 'Failed to analyze curriculum');
    }
  };

  // If no file is uploaded and no outcomes, show a message
  if (!uploadedFileInfo && outcomes.length === 0) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fff', flex: 1, minHeight: 0 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Please upload a qualification file in Step 1 before proceeding to Step 2.
        </Alert>
      </Box>
    );
  }

  // Removed the 'Extract Outcomes' gate to ensure Step 2 opens in Curriculum Review by default

  return (
    <Box sx={{ width: '100vw', height: '100%', p: 0, m: 0, display: 'flex', flexDirection: 'column', background: '#fff', flex: 1, minHeight: 0 }}>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      
      {/* ELO Mapping Canvas - No Tabs, Canvas Only */}
      <>
          {/* Complete + Export + Share */}
          {!isReadOnlyView && (
            <Box sx={{ position: 'fixed', top: 80, right: 10, zIndex: 1300, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #0002', p: 1, display: 'flex', gap: 1 }}>
              <Button variant="contained" color="success" size="small" onClick={() => setCompleteConfirmOpen(true)}>Complete</Button>
              <Button variant="outlined" color="primary" size="small" onClick={handleExportCSV}>Export CSV</Button>
              <Button variant="outlined" color="secondary" size="small" onClick={handleShareReadOnly}>Share</Button>
            </Box>
          )}

          {/* Regeneration Buttons */}
          {!isReadOnlyView && (
            <Box sx={{ position: 'fixed', top: 140, right: 10, zIndex: 1300, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #0002', p: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: '200px' }}>
              <Button 
                variant="outlined" 
                color="warning" 
                size="small" 
                onClick={handleRegenerateOutcomes}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {loading ? 'Regenerating...' : 'Regenerate ELOs'}
              </Button>
              <Button 
                variant="outlined" 
                color="info" 
                size="small" 
                onClick={handleRegenerateAssignmentsOnly}
                disabled={loading || assignWaiting}
                startIcon={loading || assignWaiting ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {loading || assignWaiting ? 'Regenerating...' : 'Regenerate Assignments'}
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                size="small" 
                onClick={handleRegenerateAll}
                disabled={loading || assignWaiting}
                startIcon={loading || assignWaiting ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {loading || assignWaiting ? 'Regenerating...' : 'Regenerate All'}
              </Button>
            </Box>
          )}

          {showAssignDebug && (
            <Box sx={{ position: 'fixed', right: 10, bottom: 10, width: 420, height: 380, background: '#0d1117', color: '#c9d1d9', borderRadius: 8, boxShadow: '0 4px 12px #0006', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1400 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: '1px solid #30363d', background: '#161b22' }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>Assignments Stream</span>
                <Box>
                  <Button size="small" color="inherit" onClick={() => setAssignLog([])} sx={{ color: '#8b949e' }}>Clear</Button>
                  <Button size="small" color="inherit" onClick={() => setShowAssignDebug(false)} sx={{ color: '#8b949e' }}>Close</Button>
                </Box>
              </Box>
              <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #30363d', background: '#0f141a' }}>
                {assignWaiting ? (
                  <Typography variant="caption" sx={{ color: '#58a6ff', fontWeight: 700 }}>Status: In progress…</Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: '#7ee787', fontWeight: 700 }}>Status: Complete</Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12, p: 1.5 }}>
                {assignLog.map((row, i) => (
                  <div key={row.t + '-' + i}>
                    <span style={{ color: '#58a6ff' }}>[{row.e}]</span> <span>{(() => { try { return JSON.stringify(row.d); } catch { return String(row.d); } })()}</span>
                  </div>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Read-only header bar with qualification title and last edit */}
          {isReadOnlyView && (
            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1400, background: '#ffffff', borderBottom: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{qualificationTitle || 'Untitled Qualification'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{qualificationLastEdit ? `Last edit: ${qualificationLastEdit}` : ''}</Typography>
            </Box>
          )}

          <ZoomPanCanvas 
            outcomes={outcomes} 
            selectedIdx={selectedIdx}
            onSelectCard={handleSelectCard}
            onOpenColorPicker={handleOpenColorPicker}
            onOpenEditModal={handleOpenEditModal}
            onUpdateModuleTitle={handleUpdateModuleTitle}
            showTasksOnCanvas={showTasksOnCanvas}
            reqNodes={reqNodes}
            ref={canvasRef}
            readOnly={(() => { try { const url = new URL(window.location.href); return url.searchParams.get('readonly') === '1'; } catch { return false; } })()}
            assignReasons={assignReasons}
            qualificationId={qualificationId}
            onMarkUserAssign={markUserAssign}
            onRefreshMeta={async ()=>{ try { const res = await api.get(`/qualifications/${qualificationId}/outcomes`); const outs = res.data?.outcomes || []; setOutcomes(outs); const meta = {}; outs.forEach((o,i)=>{ const m=o._assignment_meta||{}; Object.keys(m).forEach(code=>{ const ent=m[code]||{}; meta[`${i}|${ent.type||'KT'}|${code}`] = { reason: ent.reason || '', confidence: (typeof ent.confidence==='number'?ent.confidence:null), source: ent.source || 'ai', credits: ent.credits || 0, notional_hours: ent.notional_hours || 0, active_hours: ent.active_hours || 0, weight_pct: ent.weight_pct || 0 }; }); }); setAssignReasons(meta);} catch {} }}
            newCurriculumData={newCurriculumData}
          />
          {(assignWaiting || showAssignIndicator) && (
            <Box sx={{ position: 'fixed', right: 10, bottom: 380, zIndex: 1500, background: '#0d1117', color: '#c9d1d9', borderRadius: 2, boxShadow: '0 4px 12px #0006', display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25 }}>
              {assignWaiting && (<CircularProgress size={18} sx={{ color: '#58a6ff' }} />)}
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{assignIndicatorText || (assignWaiting ? 'Waiting for assignments…' : '')}</Typography>
            </Box>
          )}
          </>
      
      {/* Complete confirmation */}
      <Dialog open={completeConfirmOpen} onClose={() => setCompleteConfirmOpen(false)}>
        <DialogTitle>Mark ELO Mapping as Complete?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will move you to the next step. You can return later if needed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            setCompleteConfirmOpen(false);
            try {
              await api.put(`/qualifications/${qualificationId}/steps/step_3`, { status: 'completed' });
              await api.put(`/qualifications/${qualificationId}/steps/step_4`, { status: 'not_started' });
              if (typeof onRefreshSteps === 'function') {
                await onRefreshSteps();
              }
              if (typeof onAdvanceStep === 'function') {
                onAdvanceStep('step_4');
              } else {
                try { localStorage.setItem(`qualification_${qualificationId}_currentStep`, 'step_4'); } catch {}
              }
            } catch {}
          }} variant="contained" color="success">Confirm</Button>
        </DialogActions>
      </Dialog>
      {/* Full-screen loading overlay */}
      {loadingOverlay && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <CircularProgress size={64} sx={{ color: '#fff' }} />
          <Typography variant="h6" sx={{ mt: 2, color: '#fff', fontWeight: 700 }}>
            {(() => {
              const jokes = [
                'Sharpening pencils…',
                'Convincing ELOs to reveal themselves…',
                'Counting credits (twice, just to be sure)…',
                'Teaching KTs some manners…',
                'Asking PS to flex real skills…',
                'Chasing WA around the workplace…',
                'Colour-coding the chaos…',
                'Polishing module titles…',
                'Aligning stars and percentages…',
                'Making the canvas infinitely more infinite…'
              ];
              const msg = jokes[loaderTick % jokes.length];
              return `${loadingBaseMessage || 'Working…'} ${msg}`;
            })()}
          </Typography>
        </Box>
      )}

      {/* Color Picker Dialog */}
      {colorPickerOpen && (
        <Dialog open={colorPickerOpen} onClose={() => { setColorPickerOpen(false); setTempColor(null); }}>
          <DialogTitle>Pick a Color for "{outcomes[selectedIdx]?.outcome || 'Selected Outcome'}"</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Current color: {outcomes[selectedIdx]?.color || 'None'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Choose color:</Typography>
                <input 
                  type="color" 
                  value={tempColor || outcomes[selectedIdx]?.color || '#e3f2fd'}
                  onChange={e => handleChangeColor(e.target.value)} 
                  style={{ width: '50px', height: '40px', border: 'none', borderRadius: '4px' }}
                />
              </Box>
              <Box sx={{ 
                width: '100px', 
                height: '60px', 
                backgroundColor: tempColor || outcomes[selectedIdx]?.color || '#e3f2fd',
                border: '2px solid #ccc',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="caption" sx={{ color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                  Preview
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setColorPickerOpen(false); setTempColor(null); }}>Cancel</Button>
            <Button onClick={handleConfirmColor} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Share dialog */}
      {shareDialogOpen && (
        <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
          <DialogTitle>Share Read-Only Canvas</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>Anyone with this link can view the canvas, pan/zoom, and expand items. They cannot edit.</Typography>
            <TextField fullWidth value={shareLink} InputProps={{ readOnly: true }} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
            <Button onClick={() => { try { navigator.clipboard.writeText(shareLink); setSnackbar({ open: true, message: 'Link copied', severity: 'success' }); } catch {} }} variant="contained">Copy Link</Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Edit Modal */}
      {editModalOpen && (
        <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <DialogTitle>Edit Module Title & Description</DialogTitle>
          <DialogContent>
            <TextField
              label="Module Title"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Curriculum Summary Modal */}
      <CurriculumSummaryModal open={curriculumModalOpen} onClose={() => setCurriculumModalOpen(false)} qualificationId={qualificationId} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const QualificationPage = () => {
  // Sidebar open/close state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarKey, setSidebarKey] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();
  const [qualification, setQualification] = useState(null);
  const [currentStep, setCurrentStep] = useState('step_1');
  const [shouldStartStreaming, setShouldStartStreaming] = useState(false); // Trigger for canvas assignment streaming
  const [file, setFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [architectConfigured, setArchitectConfigured] = useState(false);
  const [showArchitectModal, setShowArchitectModal] = useState(false);
  const [showAIAssistantModal, setShowAIAssistantModal] = useState(false);
  const [assistantExists, setAssistantExists] = useState(false);
  const [assistantDefaults, setAssistantDefaults] = useState({});
  const [systemInstruction, setSystemInstruction] = useState('');
  // Add state for uploaded file info
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);
  // Add state for ELO document info
  const [eloFileInfo, setEloFileInfo] = useState(null);
  const [clientBriefFileInfo, setClientBriefFileInfo] = useState(null);
  const [hasExitLevelOutcomes, setHasExitLevelOutcomes] = useState(false);
  const [curriculumHasExitLevelOutcomes, setCurriculumHasExitLevelOutcomes] = useState(false);
  const [curriculumExtractNonce, setCurriculumExtractNonce] = useState(0);
  // Tabular AI summary state
  const [aiTable, setAiTable] = useState([]);
  const [aiTableColumns, setAiTableColumns] = useState([]);
  const [aiEditRow, setAiEditRow] = useState(null); // {rowIdx, field, value}
  const [aiPromptDialog, setAiPromptDialog] = useState({ open: false, rowIdx: null, prompt: '' });
  const [aiRowLoading, setAiRowLoading] = useState({}); // {rowIdx: bool}

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // --- Editable AI Response State ---
  const [editMode, setEditMode] = useState(false); // (optional, for future use)
  const [editingField, setEditingField] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPromptLoading, setAiPromptLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // JSON Tree Modal state
  const [jsonTreeModalOpen, setJsonTreeModalOpen] = useState(false);
  
  // PIN Modal state for step_5 access
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [pendingStepChange, setPendingStepChange] = useState(null);
  
  // Force shared read-only links to open correct step based on view parameter
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const viewParam = url.searchParams.get('view');
      const readonlyParam = url.searchParams.get('readonly');
      

      
      // PRIORITY: Check for readonly parameter first
      if (readonlyParam === '1') {
        if (viewParam === 'storyboard') {

          setCurrentStep('step_4'); // Storyboard view
          return; // Don't check hash
        } else if (viewParam === 'canvas') {

          setCurrentStep('step_2'); // Canvas view (ELO mapping)
          return; // Don't check hash
        } else {
          // Default to canvas for backward compatibility

        setCurrentStep('step_2');
          return; // Don't check hash
        }
      } else if (url.searchParams.get('view') === 'canvas') {
        setCurrentStep('step_2');
        return; // Don't check hash
      }
      
      // Only check hash if not in readonly mode
      const hash = window.location.hash.replace('#', '');
      if (hash && hash.startsWith('step_')) {
        // Intercept step_5 and require PIN
        if (hash === 'step_5') {
          setPendingStepChange('step_5');
          setPinModalOpen(true);
          setPinValue('');
          setPinError('');
          // Set to step_4 (storyboard) as fallback
          setCurrentStep('step_4');
        } else {
          setCurrentStep(hash);
        }
      }
    } catch (e) {

    }
  }, []);
  
  // Listen for step navigation events from complete buttons
  useEffect(() => {
    const handleNavigate = (e) => {
      if (e.detail?.step) {
        // Intercept step_5 and require PIN
        if (e.detail.step === 'step_5') {
          setPendingStepChange('step_5');
          setPinModalOpen(true);
          setPinValue('');
          setPinError('');
        } else {
          setCurrentStep(e.detail.step);
          refreshStepStatuses();
        }
      }
    };
    window.addEventListener('navigateToStep', handleNavigate);
    return () => window.removeEventListener('navigateToStep', handleNavigate);
  }, []);

  // Read-only shared canvas view detection
  const isReadOnlyView = (() => { try { const url = new URL(window.location.href); return url.searchParams.get('readonly') === '1'; } catch { return false; } })();

  // Move initialSteps and useState here
  const initialSteps = [
    { key: 'step_1', label: 'Curriculum Analysis', status: 'not_started' },
    { key: 'step_2', label: 'Qualification Overview', status: 'locked' },
    { key: 'step_3', label: 'ELO Mapping Canvas', status: 'locked' },
    { key: 'step_4', label: 'Storyboard Creation', status: 'locked' },
    { key: 'step_5', label: 'Content Creation', status: 'locked' },
    { key: 'step_6', label: 'Experience Verification', status: 'locked' },
    { key: 'step_7', label: 'Contact Sessions', status: 'locked' },
    { key: 'step_8', label: 'Exam Preparation', status: 'locked' },
    { key: 'step_9', label: 'Mystery Student Report', status: 'locked' }
  ];
  const [steps, setSteps] = useState(initialSteps);
  const [refreshing, setRefreshing] = useState(false);

  // Debug: Log steps state changes
  useEffect(() => {

    const step2 = steps.find(s => s.key === 'step_2');

  }, [steps]);

  // Function to refresh step statuses from backend
  const refreshStepStatuses = async () => {
    if (!id) return;
    
    if (refreshing) {

      return;
    }
    
    setRefreshing(true);
    try {

      const res = await api.get(`/qualifications/${id}/steps?_t=${Date.now()}`);

      const stepOrder = initialSteps.map(s => s.key);
      const sortedSteps = [...res.data].sort((a, b) => stepOrder.indexOf(a.step_key) - stepOrder.indexOf(b.step_key));
      
      // Create completely new steps array from backend data
      const updatedSteps = initialSteps.map(s => {
          const backendStep = sortedSteps.find(b => b.step_key === s.key);
        const newStep = backendStep ? { ...s, status: backendStep.status } : s;

        return newStep;
      });
      


      
      // Set the new steps array directly
      setSteps(updatedSteps);
      
      // Force sidebar to re-render with updated steps
      setSidebarKey(prev => {
        const newKey = prev + 1;

        return newKey;
      });
    } catch (error) {

    } finally {
      setRefreshing(false);
    }
  };

  // Centralized function to update step status
  const updateStepStatus = async (stepKey, status, unlockNextStep = null) => {
    try {

      
      // Update the current step
      await api.put(`/qualifications/${id}/steps/${stepKey}`, { status });
      
      // Unlock next step if specified
      if (unlockNextStep) {
        await api.put(`/qualifications/${id}/steps/${unlockNextStep}`, { status: 'not_started' });

      }
      
      // Refresh step statuses and wait for completion
      await refreshStepStatuses();
      

      return true;
    } catch (error) {

      return false;
    }
  };

  // Function to verify step status after update
  const verifyStepStatus = async (stepKey, expectedStatus) => {
    try {
      const res = await api.get(`/qualifications/${id}/steps`);
      const step = res.data.find(s => s.step_key === stepKey);
      if (step?.status !== expectedStatus) {

        return false;
      }

      return true;
    } catch (error) {

      return false;
    }
  };

  // Function to check if step_1 should be marked as completed
  const checkAndUpdateStep1Status = async () => {
    if (!id || !aiSummary) return;
    
    try {
      // If we have AI analysis data, step_1 should be completed
      if (aiSummary && aiSummary !== '') {
        await updateStepStatus('step_1', 'completed', 'step_2');
      }
    } catch (error) {

    }
  };

  // Function to ensure step 2 assistant is created
  const ensureStep2Assistant = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/qualifications/${id}/assistant/step_2`);
      return response.data;
    } catch (error) {
      try {
        // The backend should create the assistant automatically when this endpoint is called
        const response = await api.get(`/qualifications/${id}/assistant/step_2`);
        return response.data;
      } catch (createError) {

        throw createError;
      }
    }
  };

  // Utility to extract JSON from markdown code block
  function extractJsonFromCodeBlock(text) {
    if (!text) return text;
    return text
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/```\s*$/i, '')
      .trim();
  }

  // Mapping function: attempts to convert alternate OpenAI JSON to required flat schema
  function mapToQualificationSchema(data) {
    if (!data || typeof data !== 'object') {
      return null;
    }
    const requiredFields = [
      "QUALIFICATION TITLE",
      "SAQA QUAL ID",
      "QUALIFICATION TYPE",
      "FIELD",
      "SUBFIELD",
      "MINIMUM CREDITS",
      "NQF LEVEL",
      "LAST DATE FOR ENROLMENT",
      "LAST DATE FOR ACHIEVEMENT",
      "PURPOSE OF THE QUALIFICATION",
      "LEARNER OUTCOMES",
      "RATIONALE FOR THE QUALIFICATION"
    ];

    const mapped = {};
    for (const field of requiredFields) {
      if (data[field] != null && String(data[field]).trim() !== '') {
        mapped[field] = String(data[field]);
      }
    }

    const aliasMap = {
      "PURPOSE OF THE QUALIFICATION": ["PURPOSE", "PURPOSE OF QUALIFICATION"],
      "LAST DATE FOR ENROLMENT": ["REGISTRATION START DATE", "LAST DATE FOR ENROLLMENT"],
      "LAST DATE FOR ACHIEVEMENT": ["REGISTRATION END DATE"],
      "LEARNER OUTCOMES": ["LEARNING OUTCOMES", "EXIT LEVEL OUTCOMES"],
      "QUALIFICATION TITLE": ["TITLE", "title"]
    };
    for (const [canonical, aliases] of Object.entries(aliasMap)) {
      if (mapped[canonical]) continue;
      for (const alias of aliases) {
        if (data[alias] != null && String(data[alias]).trim() !== '') {
          mapped[canonical] = String(data[alias]);
          break;
        }
      }
    }

    if (Object.keys(mapped).length > 0) {
      for (const field of requiredFields) {
        if (!(field in mapped)) {
          mapped[field] = data[field] != null ? String(data[field]) : '';
        }
      }
      return mapped;
    }

    const isAlready = requiredFields.every(f => f in data);
    if (isAlready) {
      return data;
    }
    // Try to map from alternate structure
    mapped["QUALIFICATION TITLE"] = data.title || data.overview?.qualification || '';
    mapped["SAQA QUAL ID"] = data.saqa_qual_id || '';
    mapped["QUALIFICATION TYPE"] = data.qualification_type || '';
    mapped["FIELD"] = data.field || '';
    mapped["SUBFIELD"] = data.subfield || '';
    mapped["MINIMUM CREDITS"] = data.overview?.credits?.toString() || data.credits?.toString() || '';
    mapped["NQF LEVEL"] = data.overview?.NQF_level ? `Level ${data.overview.NQF_level}` : (data.nqf_level ? `Level ${data.nqf_level}` : '');
    mapped["LAST DATE FOR ENROLMENT"] = data.last_date_for_enrolment || '';
    mapped["LAST DATE FOR ACHIEVEMENT"] = data.last_date_for_achievement || '';
    mapped["PURPOSE OF THE QUALIFICATION"] = data.purpose || data.overview?.purpose || '';
    mapped["LEARNER OUTCOMES"] = data.learner_outcomes || '';
    mapped["RATIONALE FOR THE QUALIFICATION"] = data.rationale || '';
    // Try to build outcomes/rationale from other fields if missing
    if (!mapped["LEARNER OUTCOMES"] && data.curriculum_structure) {
      const km = data.curriculum_structure.knowledge_modules?.map(m => m.title).join('; ');
      const pm = data.curriculum_structure.practical_modules?.map(m => m.title).join('; ');
      const wm = data.curriculum_structure.work_experience_modules?.map(m => m.title).join('; ');
      mapped["LEARNER OUTCOMES"] = [km, pm, wm].filter(Boolean).join(' | ');
    }
    if (!mapped["RATIONALE FOR THE QUALIFICATION"] && data.key_competencies) {
      mapped["RATIONALE FOR THE QUALIFICATION"] = Object.values(data.key_competencies).flat().map(k => k.description).join(' ');
    }
    // If at least 3 required fields are present, return mapped, else null
    const presentCount = requiredFields.filter(f => mapped[f]).length;
    return presentCount >= 3 ? mapped : null;
  }

  function QualificationDetails({ data, editable, values, onFieldChange, editingField, onEditField, onCancelEdit, onSaveField }) {
    if (!data || typeof data !== 'object') return null;
    const fields = [
      "QUALIFICATION TITLE",
      "SAQA QUAL ID",
      "QUALIFICATION TYPE",
      "FIELD",
      "SUBFIELD",
      "MINIMUM CREDITS",
      "NQF LEVEL",
      "LAST DATE FOR ENROLMENT",
      "LAST DATE FOR ACHIEVEMENT",
      "PURPOSE OF THE QUALIFICATION",
      "LEARNER OUTCOMES",
      "RATIONALE FOR THE QUALIFICATION"
    ];
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Table size="small">
          <TableBody>
            {fields.map(field => (
              <TableRow key={field}>
                <TableCell sx={{ fontWeight: 'bold', width: 220 }}>{field}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-line', position: 'relative' }}>
                  {editable && editingField === field ? (
                    <Box display="flex" alignItems="center">
                      <TextField
                        value={values[field] || ''}
                        onChange={e => onFieldChange(field, e.target.value)}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                        sx={{ mr: 1 }}
                      />
                      <Tooltip title="Save"><IconButton onClick={() => onSaveField(field)} size="small"><CheckIcon /></IconButton></Tooltip>
                      <Tooltip title="Cancel"><IconButton onClick={onCancelEdit} size="small"><CloseIcon /></IconButton></Tooltip>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center">
                      <Box flex={1} sx={{ whiteSpace: 'pre-line' }}>{values[field] || ''}</Box>
                      {editable && (
                        <Tooltip title="Edit"><IconButton onClick={() => onEditField(field)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  // Parse and map the AI summary for editing
  let mappedAiSummary = null;
  try {
    // Skip parsing if aiSummary is empty, null, or undefined
    if (aiSummary && aiSummary !== '' && aiSummary !== 'null' && aiSummary !== 'undefined') {
      let parsed;
      if (typeof aiSummary === 'string') {
        // Check if the string contains markdown code blocks
        if (aiSummary.includes('```json')) {
          // Extract JSON from markdown code blocks
          const jsonMatch = aiSummary.match(/```json\s*([\s\S]*?)\s*```/);
          if (jsonMatch) {
            parsed = JSON.parse(jsonMatch[1].trim());
          } else {
            // Fallback: try to extract JSON without the code block markers
            const jsonStart = aiSummary.indexOf('{');
            const jsonEnd = aiSummary.lastIndexOf('}');
            if (jsonStart !== -1 && jsonEnd !== -1) {
              const jsonString = aiSummary.substring(jsonStart, jsonEnd + 1);
              parsed = JSON.parse(jsonString);
            } else {
              throw new Error('No valid JSON found in markdown');
            }
          }
        } else {
          // Direct JSON parsing
          parsed = JSON.parse(aiSummary);
        }
      } else {
        parsed = aiSummary;
      }
      mappedAiSummary = mapToQualificationSchema(parsed);
    }
  } catch (error) {

  }

  // Keep editedValues in sync with mappedAiSummary
  useEffect(() => {
    if (mappedAiSummary) setEditedValues(mappedAiSummary);
  }, [aiSummary]);

  // Handlers for editing
  const handleEditField = (field) => setEditingField(field);
  const handleCancelEdit = () => setEditingField(null);
  const handleFieldChange = (field, value) => setEditedValues(prev => ({ ...prev, [field]: value }));
  const handleSaveField = (field) => setEditingField(null);
  // Save function for inline edits (no longer marks step as completed)
  const handleSaveAll = async () => {
    setSaveLoading(true);
    try {
      // Only save the edited values, don't mark step as completed
      await api.put(`/qualifications/${id}`, { ai_analysis: JSON.stringify(editedValues) });
      setSaveSuccess(true);

    } catch (err) {

    }
    setSaveLoading(false);
  };

  const handleSendAIPrompt = async () => {
    setAiPromptLoading(true);
    try {
      const response = await api.post(`/qualifications/${id}/analyze`, {
        prompt: aiPrompt,
        row: editedValues
      });
      setAiSummary(JSON.stringify(response.data.ai_analysis));
      setEditedValues(response.data.ai_analysis);
      setAiPrompt('');
    } catch (err) {
      // handle error
    }
    setAiPromptLoading(false);
  };

  // Fetch assistant defaults on mount
  useEffect(() => {
    if (isReadOnlyView) return; // skip in readonly
    api.get('/qualifications/0/assistant/defaults/all')
      .then(res => {
        const map = {};
        res.data.forEach(d => { map[d.step] = d.system_instruction; });
        setAssistantDefaults(map);
      });
  }, []);

  // When a step is started, check if assistant exists; if not, create it automatically
  useEffect(() => {
    if (isReadOnlyView) return; // skip in readonly
    if (!id || !currentStep) return;
    api.get(`/qualifications/${id}/assistant/${currentStep}`)
      .then(res => {
        // Assistant exists, update state
        setAssistantExists(true);
        setArchitectConfigured(!!res.data.system_instruction);
      })
      .catch((err) => {
        // Assistant will be created automatically by the backend
        setAssistantExists(false);
        setArchitectConfigured(false);
      });
  }, [id, currentStep]);

  // Persist and fetch file info on page load
  useEffect(() => {
    if (isReadOnlyView) return; // skip in readonly
    if (!id) return;
    api.get(`/qualifications/${id}`)
      .then(res => {
        if (res.data.file_name && res.data.openai_file_id) {
          setUploadedFileInfo({
            fileName: res.data.file_name,
            fileId: res.data.openai_file_id
          });
        }
        if (res.data.elo_file_name && res.data.elo_openai_file_id) {
          setEloFileInfo({
            fileName: res.data.elo_file_name,
            fileId: res.data.elo_openai_file_id
          });
        }
        if (res.data.client_file_name && res.data.client_openai_file_id) {
          setClientBriefFileInfo({
            fileName: res.data.client_file_name,
            fileId: res.data.client_openai_file_id
          });
        }
        if (res.data.has_exit_level_outcomes !== undefined) {
          setHasExitLevelOutcomes(res.data.has_exit_level_outcomes);
        }
        if (res.data.curriculum_has_exit_level_outcomes !== undefined) {
          setCurriculumHasExitLevelOutcomes(res.data.curriculum_has_exit_level_outcomes);
        }
      });
  }, [id]);

  // Consolidated page initialization
  useEffect(() => {
    if (isReadOnlyView || !id) return;
    
    const initializePage = async () => {
      try {

        
        // 1. Load qualification data
        const qualRes = await api.get(`/qualifications/${id}`);
        setQualification(qualRes.data);
        console.log('[CurriculumDoc] qual:loaded', {
          qualificationId: id,
          file_name: qualRes.data.file_name,
          file_path: qualRes.data.file_path,
          file_size: qualRes.data.file_size,
          openai_file_id: qualRes.data.openai_file_id,
        });
        
        // Set file info from qualification data
        if (qualRes.data.file_name && qualRes.data.openai_file_id) {
          setUploadedFileInfo({
            fileName: qualRes.data.file_name,
            fileId: qualRes.data.openai_file_id
          });
        }
        if (qualRes.data.elo_file_name && qualRes.data.elo_openai_file_id) {
          setEloFileInfo({
            fileName: qualRes.data.elo_file_name,
            fileId: qualRes.data.elo_openai_file_id
          });
        }
        if (qualRes.data.client_file_name && qualRes.data.client_openai_file_id) {
          setClientBriefFileInfo({
            fileName: qualRes.data.client_file_name,
            fileId: qualRes.data.client_openai_file_id
          });
        }
        if (qualRes.data.has_exit_level_outcomes !== undefined) {
          setHasExitLevelOutcomes(qualRes.data.has_exit_level_outcomes);
        }
        if (qualRes.data.curriculum_has_exit_level_outcomes !== undefined) {
          setCurriculumHasExitLevelOutcomes(qualRes.data.curriculum_has_exit_level_outcomes);
        }
        
        // 2. Load step statuses

        const stepsRes = await api.get(`/qualifications/${id}/steps`);

        
        const stepOrder = initialSteps.map(s => s.key);
        const sortedSteps = [...stepsRes.data].sort((a, b) => stepOrder.indexOf(a.step_key) - stepOrder.indexOf(b.step_key));
        
        const updatedSteps = initialSteps.map(s => {
            const backendStep = sortedSteps.find(b => b.step_key === s.key);
          const newStep = backendStep ? { ...s, status: backendStep.status } : s;

          return newStep;
        });
        
        setSteps(updatedSteps);
        
        // 3. Load analysis data
        try {
          const analysisRes = await api.get(`/qualifications/${id}/analysis`);
          if (analysisRes.data.ai_analysis) {
            setAiSummary(analysisRes.data.ai_analysis);
          }
        } catch (analysisError) {

        }
        
        // 4. Check assistant configuration
        try {
          const assistantRes = await api.get(`/qualifications/${id}/assistant/step_1`);
          setAssistantExists(true);
          setArchitectConfigured(!!assistantRes.data.system_instruction);
        } catch (assistantError) {
          setAssistantExists(false);
          setArchitectConfigured(false);
        }
        
        // 5. Determine current step
        const savedStep = localStorage.getItem(`qualification_${id}_currentStep`);
        if (savedStep && sortedSteps.some(s => s.step_key === savedStep && (s.status === 'in_progress' || s.status === 'not_started'))) {
          // Intercept step_5 and require PIN
          if (savedStep === 'step_5') {
            setPendingStepChange('step_5');
            setPinModalOpen(true);
            setPinValue('');
            setPinError('');
            // Set to step_4 (storyboard) as fallback
            setCurrentStep('step_4');
          } else {
            setCurrentStep(savedStep);
          }

        } else {
          const firstAvailable = sortedSteps.find(s => s.status === 'in_progress' || s.status === 'not_started');
          if (firstAvailable) {
            setCurrentStep(firstAvailable.step_key);

          }
        }
        

      } catch (error) {

        if (error.response?.status === 404) {
          navigate('/');
        }
      }
    };
    
    initializePage();
  }, [id, navigate]);

  // Removed auto-start to prevent interfering with file uploads; users trigger analysis manually

  // Parse aiSummary as table if possible, normalize columns, and handle single object
  useEffect(() => {
    try {
      let raw = aiSummary;
      if (typeof raw === 'string') {
        raw = extractJsonFromCodeBlock(raw);
      }
      let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        parsed = [parsed]; // wrap single object in array
      }
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
        // Normalize columns: union of all keys
        const allKeys = Array.from(new Set(parsed.flatMap(obj => Object.keys(obj))));
        setAiTable(parsed.map(row => {
          const normalized = {};
          allKeys.forEach(k => { normalized[k] = row[k] ?? ''; });
          return normalized;
        }));
        setAiTableColumns(allKeys);
      } else {
        setAiTable([]);
        setAiTableColumns([]);
      }
    } catch {
      setAiTable([]);
      setAiTableColumns([]);
    }
  }, [aiSummary]);

  // Auto-check and update step_1 status when aiSummary is available
  useEffect(() => {
    if (aiSummary && aiSummary !== '') {
      checkAndUpdateStep1Status();
    }
  }, [aiSummary, id]);

  // Auto-check and update step_2 status when outcomes exist
  const checkAndUpdateStep2Status = async () => {
    if (!id || isReadOnlyView) return;
    
    try {
      const r = await api.get(`/qualifications/${id}/outcomes`);
      const haveOutcomes = Array.isArray(r.data?.outcomes) && r.data.outcomes.length > 0;
      
      if (haveOutcomes) {
        // Check current step_2 status
        const res = await api.get(`/qualifications/${id}/steps`);
        const step2 = res.data.find(s => s.step_key === 'step_2');
        
        if (step2 && step2.status === 'not_started') {

          await updateStepStatus('step_2', 'completed', null);
        }
      }
    } catch (error) {

    }
  };

  // Check step_2 status when component mounts or when outcomes change
  useEffect(() => {
    if (id && !isReadOnlyView && currentStep) {
      // Small delay to ensure other data has loaded
      const timer = setTimeout(() => {
        checkAndUpdateStep2Status();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [id, currentStep]);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setSelectedFileName(selectedFile.name);
    }
  };

  // Update handleUpload to store file info and set step status to in_progress
  const handleUpload = async (formData, uploadMeta = {}) => {
    const formFile = formData.get('file');
    const selectedFileName = uploadMeta.selectedFileName || (formFile instanceof File ? formFile.name : null);
    const documentType = formData.get('documentType') || 'curriculum';
    console.log('[CurriculumDoc] upload:request', {
      qualificationId: id,
      documentType,
      selectedFileName,
      selectedFileSize: formFile instanceof File ? formFile.size : null,
    });
    setLoading(true);
    try {
      const response = await api.post(`/qualifications/${id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('[CurriculumDoc] upload:response', {
        qualificationId: id,
        documentType: response.data.documentType,
        fileName: response.data.fileName,
        fileId: response.data.fileId,
        selectedFileName,
        nameMismatch: selectedFileName && response.data.fileName && selectedFileName !== response.data.fileName,
      });
      if (selectedFileName && response.data.fileName && selectedFileName !== response.data.fileName) {
        console.warn('[CurriculumDoc] upload:nameMismatch', { selectedFileName, serverFileName: response.data.fileName });
      }
      
      if (response.data.documentType === 'elo') {
        setEloFileInfo({
          fileName: response.data.fileName,
          fileId: response.data.fileId
        });
      } else if (response.data.documentType === 'client_brief') {
        setClientBriefFileInfo({
          fileName: response.data.fileName,
          fileId: response.data.fileId
        });
      } else {
        setUploadedFileInfo({
          fileName: response.data.fileName,
          fileId: response.data.fileId
        });
        // Update curriculum exit level outcomes flag if provided
        if (response.data.hasExitLevelOutcomes !== undefined) {
          setCurriculumHasExitLevelOutcomes(response.data.hasExitLevelOutcomes);
        }
      }
    } catch (error) {

      setError('Upload failed');
    }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!uploadedFileInfo || !architectConfigured) return;
    setLoading(true);
    setError('');
    setAiSummary(''); // Hide summary while loading
    try {
      // Generate AI Summary should analyze qualification metadata (not extract ELOs)

      const response = await api.post(`/qualifications/${id}/analyze`, {
        prompt: 'Summarise the attached document according to your rules. You MUST populate PURPOSE OF THE QUALIFICATION, LEARNER OUTCOMES, and RATIONALE FOR THE QUALIFICATION from any relevant sections in the document. Respond in JSON only (json).'
      });
      
      // Handle the new response format
      if (response.data.success) {
        // Use the parsed data from the response
        let analysisData;
        if (response.data.data) {
          // The data field is already a JSON string, parse it
          analysisData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
        } else if (response.data.ai_analysis) {
          // Extract JSON from the markdown code block
          const jsonMatch = response.data.ai_analysis.match(/```json\n([\s\S]*?)\n```/);
          if (jsonMatch) {
            analysisData = JSON.parse(jsonMatch[1]);
          } else {
            analysisData = response.data.ai_analysis;
          }
        }
        setAiSummary(JSON.stringify(analysisData));
        const mapped = mapToQualificationSchema(analysisData);
        if (mapped) setEditedValues(mapped);
        
        // Mark step_1 as completed since analysis was successful
        try {
          await updateStepStatus('step_1', 'completed', 'step_2');
        } catch (stepError) {

        }
      } else {
        // Fallback to the old format
        setAiSummary(response.data.ai_analysis);
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  // Extract ELOs - called after analysis is complete
  const handleExtractELOs = async () => {
    setLoading(true);
    setError('');
    
    try {

      
      // Ensure step 2 assistant exists
      await api.get(`/qualifications/${id}/assistant/step_2`);
      
      // Extract ELOs using the dedicated endpoint
      const response = await api.post(`/qualifications/${id}/extract-eols`, {
        prompt: 'Extract Exit Level Outcomes (ELOs) from the attached document. Look for ELOs labeled as EOL1, EOL2, EOL3, etc. For each ELO provide title, full description, and suggested_module_title (a clear module name). Call generate_training_structure only. JSON only (json).'
      });
      

      
      if (response.data.success) {
        const extractedOutcomes = response.data.outcomes || [];

        
        // Verify outcomes were saved to database
        try {
          const outcomesResponse = await api.get(`/qualifications/${id}/outcomes`);
          if (outcomesResponse.data?.outcomes && outcomesResponse.data.outcomes.length > 0) {

            setError(''); // Clear any previous errors
          } else {

            setError('ELOs extracted but not saved to database. Check backend logs for errors.');
          }
        } catch (reloadErr) {

          setError('Failed to verify ELOs were saved');
        }
        
        return true;
      } else {
        setError('Failed to extract ELOs');
        return false;
      }
    } catch (err) {

      setError(err.response?.data?.message || 'ELO extraction failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!file) return;
    
    setLoading(true);
    setError('');
    
    try {
      await api.put(`/qualifications/${id}`, {
        ai_analysis: aiSummary
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleArchitectSave = () => {
    setArchitectConfigured(true);
    setShowArchitectModal(false);
  };

  const handleAIAssistantSave = () => {
    setShowAIAssistantModal(false);
    // Refresh assistant info after save
    setAssistantExists(true);
  };

  const handleEditAssistant = (assistant) => {
    setSystemInstruction(assistant.instructions || assistant.system_instruction || '');
    setShowAIAssistantModal(true);
  };

  const handleProceedToNextStep = async () => {
    const currentStepIndex = steps.findIndex(s => s.key === currentStep);
    if (currentStepIndex !== -1 && currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1];
      setCurrentStep(nextStep.key);
      localStorage.setItem(`qualification_${id}_currentStep`, nextStep.key);
      
      // Refresh step statuses after changing step
      await refreshStepStatuses();
    }
  };

  // Save row handler
  const handleSaveRow = async (rowIdx) => {
    const updatedTable = [...aiTable];
    setLoading(true);
    setError('');
    try {
      await api.put(`/qualifications/${id}`, {
        ai_analysis: JSON.stringify(updatedTable)
      });
      setAiTable(updatedTable);
      setAiSummary(JSON.stringify(updatedTable)); // keep in sync
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // AI prompt handler
  const handleSendRowToAI = async (rowIdx, prompt) => {
    setAiRowLoading(r => ({ ...r, [rowIdx]: true }));
    setError('');
    try {
      const row = aiTable[rowIdx];
      const response = await api.post(`/qualifications/${id}/analyze`, {
        prompt: prompt,
        row: row
      });
      // Assume response.data.ai_analysis is the new row (object)
      const updatedTable = [...aiTable];
      updatedTable[rowIdx] = response.data.ai_analysis;
      setAiTable(updatedTable);
      setAiSummary(JSON.stringify(updatedTable)); // keep in sync
      setAiPromptDialog({ open: false, rowIdx: null, prompt: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'AI request failed');
    } finally {
      setAiRowLoading(r => ({ ...r, [rowIdx]: false }));
    }
  };

  // Table cell edit handler
  const handleEditCell = (rowIdx, field, value) => {
    const updatedTable = [...aiTable];
    updatedTable[rowIdx] = { ...updatedTable[rowIdx], [field]: value };
    setAiTable(updatedTable);
  };

  // Export handlers
  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Set up fonts and styling
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Title
    doc.setTextColor(0, 0, 0);
    doc.text('Qualification Overview', 105, 20, { align: 'center' });
    
    // Add line under title
    doc.setDrawColor(0, 0, 0);
    doc.line(20, 25, 190, 25);
    
    // Reset font size for content
    doc.setFontSize(12);
    let y = 40;
    
    // Define field order and grouping
    const fieldGroups = [
      {
        title: 'Basic Information',
        fields: ['QUALIFICATION TITLE', 'SAQA QUAL ID', 'QUALIFICATION TYPE', 'FIELD', 'SUBFIELD']
      },
      {
        title: 'Credits and Level',
        fields: ['MINIMUM CREDITS', 'NQF LEVEL']
      },
      {
        title: 'Important Dates',
        fields: ['LAST DATE FOR ENROLMENT', 'LAST DATE FOR ACHIEVEMENT']
      },
      {
        title: 'Qualification Details',
        fields: ['PURPOSE OF THE QUALIFICATION', 'LEARNER OUTCOMES', 'RATIONALE FOR THE QUALIFICATION']
      }
    ];
    
    fieldGroups.forEach((group, groupIndex) => {
      // Group title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(group.title, 20, y);
      y += 8;
      
      // Group fields
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      group.fields.forEach(field => {
        const value = editedValues[field] || '';
        
        // Field name
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`${field}:`, 20, y);
        y += 6;
        
        // Field value
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        
        if (value) {
          const lines = doc.splitTextToSize(value, 170);
          lines.forEach(line => {
            if (y > 270) {
              doc.addPage();
              y = 20;
            }
            doc.text(line, 25, y);
            y += 5;
          });
        } else {
          doc.text('Not specified', 25, y);
          y += 5;
        }
        
        y += 3; // Space between fields
      });
      
      y += 5; // Space between groups
      
      // Add page break if needed
      if (y > 250 && groupIndex < fieldGroups.length - 1) {
        doc.addPage();
        y = 20;
      }
    });
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 290, { align: 'center' });
    }
    
    doc.save('qualification-analysis.pdf');
  };
  
  const handleExportWord = () => {
    let html = '<html><head><meta charset="utf-8"></head><body>';
    Object.entries(editedValues).forEach(([key, value]) => {
      html += `<b>${key}:</b><br/><div>${(value || '').replace(/\n/g, '<br/>')}</div><br/>`;
    });
    html += '</body></html>';
    const blob = new Blob([html], { type: 'application/msword' });
    saveAs(blob, 'ai-summary.doc');
  };
  
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet([editedValues]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'ai-summary.xlsx');
  };
  
  const handleExportPPT = () => {
    alert('PowerPoint export is not supported in this browser version.');
  };

  // Export CSV handled within Step2

  // Share read-only canvas link (simple local storage flag)
  const handleShareReadOnly = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('readonly', '1');
      navigator.clipboard.writeText(url.toString());
      alert('Share link copied to clipboard');
    } catch {
      alert('Failed to copy share link');
    }
  };

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Debug output for qualification and step status after refresh
  useEffect(() => {
    if (!id) return;
    api.get(`/qualifications/${id}`).then(res => {
    });
    api.get(`/qualifications/${id}/steps`).then(res => {
    });
  }, [id]);

  // In read-only view, don't require qualification to be loaded before rendering
  if (!qualification && !isReadOnlyView) {

    return <CircularProgress />;
  }
  


  // Render content for the current step
  const renderStepContent = () => {
    const currentStepData = steps.find(s => s.key === currentStep);

    
    switch (currentStep) {
      case 'step_1':
        // NEW STEP 1: Curriculum Analysis (upload + curriculum tree view)
        return (
          <CurriculumAnalysisStep
            qualificationId={id}
            onUpload={handleUpload}
            loading={loading}
            uploadedFileInfo={uploadedFileInfo}
            eloFileInfo={eloFileInfo}
            clientBriefFileInfo={clientBriefFileInfo}
            curriculumHasExitLevelOutcomes={curriculumHasExitLevelOutcomes}
            onCurriculumExitLevelOutcomesChange={setCurriculumHasExitLevelOutcomes}
            hasExitLevelOutcomes={hasExitLevelOutcomes}
            onExitLevelOutcomesChange={setHasExitLevelOutcomes}
            setJsonTreeModalOpen={setJsonTreeModalOpen}
            extractNonce={curriculumExtractNonce}
            onRegenerateCurriculum={async () => {
              try {
                console.log('[CurriculumDoc] extract:start', {
                  qualificationId: id,
                  uploadedFileName: uploadedFileInfo?.fileName,
                  storedFilePath: qualification?.file_path,
                  storedFileName: qualification?.file_name,
                });
                setLoading(true);
                const extractRes = await api.post(`/qualifications/${id}/curriculum/extract/new`);
                console.log('[CurriculumDoc] extract:complete', {
                  qualificationId: id,
                  success: extractRes.data?.success,
                  alreadyExtracted: extractRes.data?.alreadyExtracted,
                  debug: extractRes.data?.debug,
                  parsedMeta: extractRes.data?.parsedMeta,
                });
                if (extractRes.data?.parsedMeta) {
                  console.log('[CurriculumDoc] extract:parsedTotals', extractRes.data.parsedMeta);
                }
                setCurriculumExtractNonce((n) => n + 1);
                setLoading(false);
                if (extractRes.data?.alreadyExtracted) {
                  return;
                }
                alert('Curriculum extracted successfully.');
              } catch (error) {
                console.error('[CurriculumDoc] extract:failed', {
                  qualificationId: id,
                  message: error.response?.data?.error || error.message,
                  detail: error.response?.data,
                });
                setLoading(false);
                alert('Failed to extract curriculum. Please try again.');
              }
            }}
            onProceedToNextStep={async () => {
              try {
                // Use centralized step update function
                const success = await updateStepStatus('step_1', 'completed', 'step_2');
                if (success) {
                  setCurrentStep('step_2');
                  try { localStorage.setItem(`qualification_${id}_currentStep`, 'step_2'); } catch {}
                } else {

                }
              } catch (error) {

              }
            }}
          />
        );
      case 'step_2':
        // NEW STEP 2: Qualification Overview (AI analysis, no upload)
        return (
          <Step1Interface
            qualificationId={id}
            onAnalyze={handleAnalyze}
            loading={loading}
            uploadedFileInfo={uploadedFileInfo}
            architectConfigured={architectConfigured}
            aiSummary={aiSummary}
            mappedAiSummary={mappedAiSummary}
            editedValues={editedValues}
            onFieldChange={handleFieldChange}
            editingField={editingField}
            onEditField={handleEditField}
            onCancelEdit={handleCancelEdit}
            onSaveField={handleSaveField}
            onSaveAll={handleSaveAll}
            saveLoading={saveLoading}
            aiPrompt={aiPrompt}
            onAiPromptChange={(e) => setAiPrompt(e.target.value)}
            onSendAIPrompt={handleSendAIPrompt}
            aiPromptLoading={aiPromptLoading}
            onExportPDF={handleExportPDF}
            onExportWord={handleExportWord}
            onExportExcel={handleExportExcel}
            currentStep={currentStep}
            steps={steps}
            onProceedToNextStep={async () => {
              try {
                // Auto-save any edited values before proceeding

                try {
                  await api.put(`/qualifications/${id}`, { ai_analysis: JSON.stringify(editedValues) });

                } catch (saveErr) {

                  // Continue anyway - save errors shouldn't block progression
                }
                
                // Check if outcomes exist, if not extract them automatically
                let haveOutcomes = false;
                let outcomeCount = 0;
                try {
                  const r = await api.get(`/qualifications/${id}/outcomes`);
                  haveOutcomes = Array.isArray(r.data?.outcomes) && r.data.outcomes.length > 0;
                  outcomeCount = r.data?.outcomes?.length || 0;

                } catch (err) {

                }
                
                if (!haveOutcomes) {
                  // Only extract ELOs if they don't exist

                  const extractSuccess = await handleExtractELOs();
                  if (!extractSuccess) {
                    alert('Failed to extract ELOs. Please check the error message above and try again.');
                    return;
                  }

                } else {
                  // Outcomes exist - do NOT clear or regenerate assignments


                }
                
                // Do NOT clear existing assignments - preserve user's work
                // User can regenerate assignments from the canvas manually if needed
                
                // Mark step_2 as completed and unlock step_3
                const success = await updateStepStatus('step_2', 'completed', 'step_3');
                if (success) {
                  // Advance to step_3 (canvas)
                  setCurrentStep('step_3');
                  try { localStorage.setItem(`qualification_${id}_currentStep`, 'step_3'); } catch {}
                } else {

                }
                
                // Only trigger assignment streaming if there are no existing assignments
                // This preserves user's work and allows manual regeneration from canvas
                let hasAssignments = false;
                try {
                  const outcomesRes = await api.get(`/qualifications/${id}/outcomes`);
                  if (outcomesRes.data?.outcomes) {
                    hasAssignments = outcomesRes.data.outcomes.some(o => 
                      (o.knowledge_tasks && o.knowledge_tasks.length > 0) ||
                      (o.practical_work && o.practical_work.length > 0) ||
                      (o.workplace_experience && o.workplace_experience.length > 0)
                    );
                  }
                } catch (err) {

                }
                
                if (!hasAssignments) {
                  // No assignments exist, trigger streaming to generate them

                  setTimeout(() => {
                    setShouldStartStreaming(true);
                  }, 100);
                } else {


                }
              } catch (error) {

              }
            }}
          />
        );
      case 'step_3':
        // NEW STEP 3: ELO Mapping Canvas (canvas only, no tabs)
        return (
          <Step2 
            qualificationId={id} 
            uploadedFileInfo={uploadedFileInfo} 
            eloFileInfo={eloFileInfo} 
            hasExitLevelOutcomes={hasExitLevelOutcomes} 
            curriculumHasExitLevelOutcomes={curriculumHasExitLevelOutcomes}
            setPreviewData={setPreviewData}
            setPreviewOpen={setPreviewOpen}
            setJsonTreeModalOpen={setJsonTreeModalOpen}
            shouldStartStreaming={shouldStartStreaming}
            setShouldStartStreaming={setShouldStartStreaming}
            onAdvanceStep={async (nextKey) => {
              setCurrentStep(nextKey);
              try { localStorage.setItem(`qualification_${id}_currentStep`, nextKey); } catch {}
            }}
            onRefreshSteps={async () => {
              await refreshStepStatuses();
            }}
          />
        );
      case 'step_4':
        // STEP 4: Storyboard Creation
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: isReadOnlyView ? 'calc(100vh - 64px - 32px)' : 'calc(100vh - 120px - 32px)', mt: isReadOnlyView ? 0 : 2 }}>
            {!isReadOnlyView && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Storyboard Creation</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ErrorBoundary>
                <StoryBoardFlow qualificationId={id} />
              </ErrorBoundary>
            </Box>
          </Box>
        );
      case 'step_5':
        // STEP 5: Learning Content Creation
        return (
          <ErrorBoundary>
            <ContentCreationStep qualificationId={id} />
          </ErrorBoundary>
        );
      case 'step_6':
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Assessments</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This step will contain the assessments creation interface.
            </Typography>
          </Box>
        );
      case 'step_6':
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Experience Verification</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This step will contain the experience verification interface.
            </Typography>
          </Box>
        );
      case 'step_7':
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Contact Sessions</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This step will contain the contact sessions interface.
            </Typography>
          </Box>
        );
      case 'step_8':
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Exam Preparation</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This step will contain the exam preparation interface.
            </Typography>
          </Box>
        );
      case 'step_9':
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">Mystery Student Report</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This step will contain the mystery student report interface.
            </Typography>
          </Box>
        );
      default:
        return (
          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">{currentStepData?.label || 'Unknown Step'}</Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Content for this step will be implemented soon.
            </Typography>
          </Box>
        );
    }
  };

  // Simple modal for code preview
  const JsonDetails = ({ value, name }) => {
    if (value === null || value === undefined) {
      return <span style={{ fontFamily: 'monospace' }}>{String(value)}</span>;
    }
    if (typeof value !== 'object') {
      return <span style={{ fontFamily: 'monospace' }}>{typeof value === 'string' ? JSON.stringify(value) : String(value)}</span>;
    }
    const entries = Array.isArray(value) ? value.map((v, i) => [i, v]) : Object.entries(value);
  return (
      <details open={false} style={{ marginLeft: name !== undefined ? 8 : 0 }}>
        {name !== undefined && (
          <summary style={{ cursor: 'pointer', fontFamily: 'monospace' }}>{Array.isArray(value) ? `${name} [${entries.length}]` : `${name}`}</summary>
        )}
        <div style={{ paddingLeft: 12 }}>
          {entries.map(([k, v], idx) => (
            <div key={`${k}-${idx}`} style={{ margin: '2px 0' }}>
              {typeof v === 'object' && v !== null ? (
                <JsonDetails value={v} name={k} />
              ) : (
                <span style={{ fontFamily: 'monospace' }}>
                  <strong>{k}</strong>: {typeof v === 'string' ? JSON.stringify(v) : String(v)}
                </span>
              )}
            </div>
          ))}
        </div>
      </details>
    );
  };
  const PreviewModal = () => (
    <div style={{ display: previewOpen ? 'block' : 'none', position: 'fixed', inset: '64px 0 0 0', background: 'rgba(0,0,0,0.6)', zIndex: 1000, padding: 24, overflow: 'auto' }}>
      <div style={{ maxWidth: 1100, margin: '40px auto', background: '#fff', borderRadius: 8, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Code-Guided Extraction Preview</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={async () => {
                try {
                  const token = (() => { try { const t = localStorage.getItem('token'); return t && t !== 'undefined' && t !== 'null' && t.trim() !== ''; } catch { return false; } })();
                  if (isReadOnlyView || !token) {
                    alert('You are in read-only mode or not signed in. Please sign in to commit.');
                    return;
                  }
                  const payload = (() => {
                    if (!previewData) return {};
                    if (Array.isArray(previewData)) return previewData;
                    if (previewData.preview && Array.isArray(previewData.preview)) return { preview: previewData.preview };
                    return previewData;
                  })();
                  await api.post(`/qualifications/${id}/codes/commit`, payload);
                  alert('Committed preview to database.');
                  try { window.dispatchEvent(new CustomEvent('nodes:refresh', { detail: { qualificationId: id } })); } catch {}
                } catch (e) {
                  alert(e?.response?.data?.message || 'Commit failed');
                }
              }}
              style={{ fontSize: 14 }}
              disabled={(() => { try { const t = localStorage.getItem('token'); const has = t && t !== 'undefined' && t !== 'null' && t.trim() !== ''; return isReadOnlyView || !has; } catch { return true; } })()}
            >
              Commit Preview to Database
            </button>
            <button onClick={() => setPreviewOpen(false)} style={{ fontSize: 16 }}>Close</button>
          </div>
        </div>
        {(isReadOnlyView || (() => { try { const t = localStorage.getItem('token'); return !t || t === 'undefined' || t === 'null' || t.trim() === ''; } catch { return true; } })()) && (
          <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7' }}>
            Read-only mode or no sign-in detected. Commit is disabled. Please sign in to persist nodes.
          </div>
        )}
        <div style={{ marginTop: 12, maxHeight: 600, overflow: 'auto' }}>
          <JsonDetails value={previewData || {}} name={'preview'} />
        </div>
      </div>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {!isReadOnlyView && (
        <AppBar position="fixed">
          <Toolbar>
            <SidebarHamburger onClick={async () => {

              await refreshStepStatuses();
              setSidebarOpen(true);
            }} />
            <IconButton color="inherit" onClick={() => window.location.href = '/'} sx={{ mr: 2 }}>
              <HomeIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{qualification?.title || 'Qualification'}</Typography>
            <IconButton color="inherit" onClick={handleLogout}><LogoutIcon /></IconButton>
          </Toolbar>
        </AppBar>
      )}
      {!isReadOnlyView && (
        <StepSidebar
          key={`sidebar-${sidebarKey}`}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        currentStep={currentStep}
        steps={steps}
        onStepChange={(key) => {
          // Intercept step_5 and require PIN
          if (key === 'step_5') {
            setPendingStepChange(key);
            setPinModalOpen(true);
            setPinValue('');
            setPinError('');
          } else {
            setCurrentStep(key);
            try { localStorage.setItem(`qualification_${id}_currentStep`, key); } catch {}
          }
        }}
        />
      )}
      <Box sx={{ flex: 1, mt: isReadOnlyView ? 0 : '64px', pb: '32px', overflow: 'auto' }}>
        <PreviewModal />
        {renderStepContent()}
      </Box>
      
      {/* JSON Tree Modal */}
      <JsonTreeModal
        open={jsonTreeModalOpen}
        onClose={() => setJsonTreeModalOpen(false)}
        qualificationId={id}
      />
      
      {/* PIN Modal for step_5 access */}
      <Dialog 
        open={pinModalOpen} 
        onClose={() => {
          setPinModalOpen(false);
          setPinValue('');
          setPinError('');
          setPendingStepChange(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter PIN to Access Learning Content Creation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please enter the PIN to proceed to the Learning Content Creation step.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="PIN"
            type="password"
            fullWidth
            variant="outlined"
            value={pinValue}
            onChange={(e) => {
              setPinValue(e.target.value);
              setPinError('');
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (pinValue === '9999') {
                  setPinModalOpen(false);
                  setPinValue('');
                  setPinError('');
                  if (pendingStepChange) {
                    setCurrentStep(pendingStepChange);
                    try { localStorage.setItem(`qualification_${id}_currentStep`, pendingStepChange); } catch {}
                  }
                  setPendingStepChange(null);
                } else {
                  setPinError('Incorrect PIN. Please try again.');
                  setPinValue('');
                }
              }
            }}
            error={!!pinError}
            helperText={pinError}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setPinModalOpen(false);
              setPinValue('');
              setPinError('');
              setPendingStepChange(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (pinValue === '9999') {
                setPinModalOpen(false);
                setPinValue('');
                setPinError('');
                if (pendingStepChange) {
                  setCurrentStep(pendingStepChange);
                  try { localStorage.setItem(`qualification_${id}_currentStep`, pendingStepChange); } catch {}
                }
                setPendingStepChange(null);
              } else {
                setPinError('Incorrect PIN. Please try again.');
                setPinValue('');
              }
            }}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QualificationPage;